import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, withCtx as _withCtx, createSlots as _createSlots, toDisplayString as _toDisplayString, withModifiers as _withModifiers, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "auth-error"
}
const _hoisted_2 = {
  key: 2,
  class: "error-message"
}
const _hoisted_3 = {
  key: 3,
  class: "pagination-controls"
}

import { ref, onMounted, computed, watch, nextTick } from 'vue'
import { useTransactionsStore } from '@/stores/transactions'
import { useAuthStore } from '@/stores/auth'
import { useLoaderStore } from '@/stores/loader'
import { useToast } from '@/composables/useToast'
import { useI18n } from 'vue-i18n'
import TransactionsTable from '@/components/wagers/TransactionsTable.vue'
import type { Transaction } from '@/types/transaction'
import BaseButton from '@/components/BaseButton.vue'
import { useRoute } from 'vue-router'
import { iframeCommunication } from '@/utils/iframeCommunication'
import BaseModal from '@/components/BaseModal.vue'
import TransactionDetails from '@/components/wagers/TransactionDetails.vue'
import SectionHeader from '@/components/base/SectionHeader.vue'
import WagerStatusFilters from '@/components/wagers/WagerStatusFilters.vue'
import type { WagerStatusFilter } from '@/stores/transactions'
import { useRebetFromTransaction } from '@/composables/useRebetFromTransaction'


export default /*@__PURE__*/_defineComponent({
  __name: 'WagersView',
  setup(__props) {

const transactionsStore = useTransactionsStore()
const authStore = useAuthStore()
const loaderStore = useLoaderStore()
const { showToast } = useToast()
const { t } = useI18n()

const onCopySuccess = () => {
  showToast({
    title: t('transactionDetails.textCopied'),
    message: '',
    type: 'success',
    duration: 2500,
    centered: true,
  })
}

const onCopyFailed = () => {
  showToast({
    title: t('common.error'),
    message: t('transactionDetails.copyFailed'),
    type: 'error',
    duration: 4000,
    centered: true,
  })
}

const { applyRebetFromCurrentTransaction, isRebetting } = useRebetFromTransaction()
const route = useRoute()
const isDetailsModalOpen = ref(false)
const savedScrollPosition = ref(0)
const wagersTopRef = ref<HTMLElement | null>(null)

// Get the expand reference from query params (for auto-opening modal)
const expandReference = ref<string | null>(route.query.expand ? (route.query.expand as string) : null)

const isAuthenticated = computed(() => authStore.isAuthenticated)
const currentPage = computed(() => Number(transactionsStore.pagination.current_page) || 1)
const totalPages = computed(() => Number(transactionsStore.pagination.total_pages) || 1)
const hasWagerHistory = computed(() => {
  return transactionsStore.transactions.length > 0 || Number(transactionsStore.pagination.total_count) > 0
})

const wagerStatusFilter = computed(
  () => transactionsStore.filters.wagerStatus ?? 'all'
)

const onWagerStatusFilterChange = async (value: WagerStatusFilter) => {
  if (transactionsStore.loading || value === wagerStatusFilter.value) {
    return
  }

  transactionsStore.setWagerStatusFilter(value)
  transactionsStore.pagination.current_page = 1
  scrollWagersViewToTop()
  loaderStore.show(t('wagers.loadingTransactions'))

  try {
    await transactionsStore.fetchTransactions(1)
  } finally {
    loaderStore.hide()
  }

  await scrollWagersViewToTopAfterLoad()
}

const handleLoginRequest = () => {
  iframeCommunication.sendLoginRequest('wagers-view-required')
  return;
}

// Watch for transactions to be loaded and auto-expand if needed
watch(
  () => transactionsStore.transactions,
  (transactions) => {
    if (transactions && transactions.length > 0 && expandReference.value && !isDetailsModalOpen.value) {
      const transaction = transactions.find(t => t.reference === expandReference.value)
      if (transaction) {
        viewDetails(transaction)
      }
    }
  },
  { immediate: true }
)

const scrollWagersViewToTop = (smooth = true) => {
  const behavior = smooth ? 'smooth' : 'auto'

  if (document.activeElement instanceof HTMLElement) {
    document.activeElement.blur()
  }

  wagersTopRef.value?.scrollIntoView({ behavior, block: 'start' })

  if (iframeCommunication.isRunningInIframe()) {
    iframeCommunication.sendScrollToTop()
    return
  }

  window.scrollTo({ top: 0, behavior })
}

const scrollWagersViewToTopAfterLoad = async () => {
  await nextTick()
  requestAnimationFrame(() => {
    scrollWagersViewToTop(true)
    if (iframeCommunication.isRunningInIframe()) {
      window.setTimeout(() => scrollWagersViewToTop(true), 350)
    }
  })
}

const viewDetails = async (wager: Transaction) => {
  try {
    savedScrollPosition.value = window.pageYOffset || document.documentElement.scrollTop
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur()
    }
    loaderStore.show(t('wagers.loadingDetails'))
    await transactionsStore.fetchTransactionDetail(wager.reference)
    isDetailsModalOpen.value = true
  } catch (err) {
    isDetailsModalOpen.value = false
  } finally {
    loaderStore.hide()
  }
}

const closeDetailsModal = () => {
  isDetailsModalOpen.value = false
  nextTick(() => {
    window.scrollTo({
      top: savedScrollPosition.value,
      behavior: 'auto'
    })
  })
}

const goToPage = async (page: number) => {
  if (transactionsStore.loading) return

  scrollWagersViewToTop()
  loaderStore.show(t('wagers.loadingTransactions'))

  transactionsStore.pagination.current_page = page
  try {
    await transactionsStore.fetchTransactions(page)
  } finally {
    loaderStore.hide()
  }

  await scrollWagersViewToTopAfterLoad()
}

const handleRebet = () =>
  applyRebetFromCurrentTransaction({
    onComplete: () => {
      isDetailsModalOpen.value = false
    },
    openBetslip: true,
  })

onMounted(async () => {
  try {
    // Iframe embeds always pass `account` (tenant/theme); that is not user login.
    // Only load wagers when we have a token (URL or store) or a full session pair.
    const hasTokenInUrl = !!route.query.token
    const canLoadWagers =
      hasTokenInUrl || authStore.isAuthenticated || authStore.hasValidSession()

    if (!canLoadWagers) {
      return
    }
    
    // const authSuccess = await authStore.authenticate()
    
    // if (!authSuccess) {
    //   showToast({
    //     title: 'Authentication Failed',
    //     message: 'Invalid authentication parameters. Please check your token and account.',
    //     type: 'error',
    //     duration: 8000
    //   })
    //   return
    // }
    
    await transactionsStore.fetchTransactions()
    
    // Check if there's an expand query parameter to auto-expand a transaction
    if (expandReference.value && !isDetailsModalOpen.value) {
      // Find the transaction with this reference
      const transaction = transactionsStore.transactions.find(t => t.reference === expandReference.value)
      if (transaction) {
        // Auto-expand this transaction
        await viewDetails(transaction)
      }
    }
  } catch (err: any) {
    console.log(err)
    
    // Check if it's a 401 error (authentication failed)
    // if (err?.response?.status === 401) {
      // Clear invalid credentials
      // authStore.clearInvalidCredentials()
      // showToast({
      //   title: 'Authentication Failed',
      //   message: 'Invalid token or account. Please check your credentials.',
      //   type: 'error',
      //   duration: 8000
      // })
      // Don't return here - let the component render the auth error message
    // } else {
    //   showToast({
    //     title: 'Error',
    //     message: 'Failed to load wagers. Please try again.',
    //     type: 'error'
    //   })
    // }
  } finally {
    loaderStore.hide()
  }
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    ref_key: "wagersTopRef",
    ref: wagersTopRef,
    class: "wagers-view"
  }, [
    _createVNode(SectionHeader, {
      title: _ctx.$t('wagers.title'),
      variant: "complex",
      class: "wagers-section-header"
    }, _createSlots({ _: 2 }, [
      (isAuthenticated.value)
        ? {
            name: "right",
            fn: _withCtx(() => [
              _createVNode(WagerStatusFilters, {
                "model-value": wagerStatusFilter.value,
                counts: _unref(transactionsStore).statusCounts,
                "onUpdate:modelValue": onWagerStatusFilterChange
              }, null, 8, ["model-value", "counts"])
            ]),
            key: "0"
          }
        : undefined
    ]), 1032, ["title"]),
    (!isAuthenticated.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
          _createElementVNode("p", null, [
            _createTextVNode(_toDisplayString(_ctx.$t('auth.authenticationRequired')) + " ", 1),
            _createElementVNode("a", {
              href: "#",
              class: "login-link",
              onClick: _withModifiers(handleLoginRequest, ["prevent"])
            }, _toDisplayString(_ctx.$t('auth.pleaseLoginLink')), 1),
            _cache[2] || (_cache[2] = _createTextVNode(". "))
          ])
        ]))
      : (_openBlock(), _createBlock(TransactionsTable, {
          key: 1,
          transactions: _unref(transactionsStore).transactions,
          loading: _unref(transactionsStore).loading,
          onViewDetails: viewDetails,
          onRebet: handleRebet,
          onCopySuccess: onCopySuccess,
          onCopyFailed: onCopyFailed
        }, null, 8, ["transactions", "loading"])),
    _createVNode(BaseModal, {
      "is-open": isDetailsModalOpen.value,
      title: _ctx.$t('wagers.details'),
      "full-screen": "",
      "hide-footer": "",
      "flush-body": "",
      onClose: closeDetailsModal
    }, {
      default: _withCtx(() => [
        (_unref(transactionsStore).currentTransaction)
          ? (_openBlock(), _createBlock(TransactionDetails, {
              key: 0,
              transaction: _unref(transactionsStore).currentTransaction,
              "is-rebetting": _unref(isRebetting),
              onRebet: handleRebet,
              onClose: closeDetailsModal
            }, null, 8, ["transaction", "is-rebetting"]))
          : _createCommentVNode("", true)
      ]),
      _: 1
    }, 8, ["is-open", "title"]),
    (_unref(transactionsStore).error)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, _toDisplayString(_unref(transactionsStore).error), 1))
      : _createCommentVNode("", true),
    (isAuthenticated.value && hasWagerHistory.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
          (currentPage.value > 1)
            ? (_openBlock(), _createBlock(BaseButton, {
                key: 0,
                variant: "outline",
                class: "pagination-button pagination-button-prev",
                disabled: _unref(transactionsStore).loading,
                onClick: _cache[0] || (_cache[0] = ($event: any) => (goToPage(currentPage.value - 1)))
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_ctx.$t('common.previous')), 1)
                ]),
                _: 1
              }, 8, ["disabled"]))
            : _createCommentVNode("", true),
          _createElementVNode("span", null, _toDisplayString(_ctx.$t('common.page')) + " " + _toDisplayString(currentPage.value) + " " + _toDisplayString(_ctx.$t('common.of')) + " " + _toDisplayString(totalPages.value), 1),
          (currentPage.value < totalPages.value)
            ? (_openBlock(), _createBlock(BaseButton, {
                key: 1,
                variant: "outline",
                class: "pagination-button pagination-button-next",
                disabled: _unref(transactionsStore).loading,
                onClick: _cache[1] || (_cache[1] = ($event: any) => (goToPage(currentPage.value + 1)))
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_ctx.$t('common.next')), 1)
                ]),
                _: 1
              }, 8, ["disabled"]))
            : _createCommentVNode("", true)
        ]))
      : _createCommentVNode("", true)
  ], 512))
}
}

})